import {
  AuthResponse,
  BulkProductUpdatePayload,
  CalculationMethod,
  CalculationResponse,
  Category,
  CurrentUserProfile,
  DemoComponentItem,
  DemoPriceListRow,
  DemoProduct,
  DemoSemiFinishedProduct,
  Packaging,
  PlatformAuthResponse,
  PriceListRow,
  Product,
  ProductComponent,
  RawMaterial,
  RawMaterialCostHistory,
  SalesChannel,
  SalesChannelParameter,
  SemiFinishedProduct,
  Tenant,
  TenantUserCreatePayload,
  TenantUserUpdatePayload,
  TradingGood,
  User,
} from './types'

const getApiBase = (): string => {
  const envUrl = import.meta.env.VITE_API_BASE_URL
  if (import.meta.env.PROD) {
    if (envUrl && !envUrl.includes('localhost')) {
      return envUrl.replace(/\/+$/, '')
    }
    return 'https://api.parel.com.tr'
  }
  return (envUrl || 'http://localhost:8000').replace(/\/+$/, '')
}

const API_BASE = getApiBase()
const BASE_PATH = `${API_BASE}/api/v1`

let activeTenantId: string | null = null
let authToken: string | null = null

export function setAuthToken(token: string | null) {
  authToken = token
  if (token) {
    localStorage.setItem('cost_auth_token', token)
  } else {
    localStorage.removeItem('cost_auth_token')
  }
}

export function getAuthToken(): string | null {
  if (!authToken) {
    authToken = localStorage.getItem('cost_auth_token')
  }
  return authToken
}

export function clearAuthSession() {
  authToken = null
  activeTenantId = null
  localStorage.removeItem('cost_auth_token')
  localStorage.removeItem('cost_active_tenant_id')
}

export function setActiveTenant(tenantId: string | null) {
  activeTenantId = tenantId
  if (tenantId) {
    localStorage.setItem('cost_active_tenant_id', tenantId)
  } else {
    localStorage.removeItem('cost_active_tenant_id')
  }
}

export function getActiveTenant(): string | null {
  if (!activeTenantId) {
    activeTenantId = localStorage.getItem('cost_active_tenant_id')
  }
  return activeTenantId
}

export function downloadBlob(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  window.URL.revokeObjectURL(url)
  document.body.removeChild(a)
}

function formatErrorDetail(body: any, defaultMsg: string): string {
  if (!body || body.detail === undefined || body.detail === null) {
    return defaultMsg
  }
  if (typeof body.detail === 'string') {
    return body.detail
  }
  if (Array.isArray(body.detail)) {
    return body.detail
      .map((item: any) => {
        if (typeof item === 'string') return item
        if (item && item.msg) return item.msg
        return JSON.stringify(item)
      })
      .join(', ')
  }
  if (typeof body.detail === 'object') {
    return JSON.stringify(body.detail)
  }
  return String(body.detail)
}

async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
    ...(options.headers as Record<string, string>),
  }

  const token = getAuthToken()
  if (token && !headers['Authorization']) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const tenantId = getActiveTenant()
  if (tenantId && !headers['X-Tenant-ID']) {
    headers['X-Tenant-ID'] = tenantId
  }

  let res: Response
  try {
    res = await fetch(`${BASE_PATH}${endpoint}`, {
      cache: 'no-store',
      ...options,
      headers,
    })
  } catch (netErr: any) {
    throw new Error(
      `Sunucuya erişilemedi (${endpoint}): ${netErr?.message || 'Ağ bağlantısı veya CORS hatası'}. Lütfen internet bağlantınızı ve sunucunun çalıştığını kontrol edin.`
    )
  }

  if (!res.ok) {
    if (res.status === 401) {
      clearAuthSession()
    }
    let errorDetail = `İstek başarısız oldu: ${res.status}`
    try {
      const body = await res.json()
      errorDetail = formatErrorDetail(body, errorDetail)
    } catch {
      // ignore
    }
    throw new Error(errorDetail)
  }

  if (res.status === 204) {
    return {} as T
  }

  return res.json()
}

async function downloadFile(endpoint: string, options: RequestInit = {}, defaultFilename: string): Promise<void> {
  const headers: Record<string, string> = {
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
    ...(options.headers as Record<string, string>),
  }

  const token = getAuthToken()
  if (token && !headers['Authorization']) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const tenantId = getActiveTenant()
  if (tenantId && !headers['X-Tenant-ID']) {
    headers['X-Tenant-ID'] = tenantId
  }

  let res: Response
  try {
    res = await fetch(`${BASE_PATH}${endpoint}`, {
      cache: 'no-store',
      ...options,
      headers,
    })
  } catch (netErr: any) {
    throw new Error(
      `Dosya indirilemedi (${endpoint}): ${netErr?.message || 'Ağ bağlantısı hatası'}`
    )
  }

  if (!res.ok) {
    if (res.status === 401) {
      clearAuthSession()
    }
    let errorDetail = `İndirme başarısız oldu: ${res.status}`
    try {
      const body = await res.json()
      errorDetail = formatErrorDetail(body, errorDetail)
    } catch {
      // ignore
    }
    throw new Error(errorDetail)
  }

  const contentDisposition = res.headers.get('Content-Disposition')
  let filename = defaultFilename
  if (contentDisposition) {
    const match = contentDisposition.match(/filename="?([^";]+)"?/)
    if (match && match[1]) filename = match[1]
  }

  const blob = await res.blob()
  downloadBlob(blob, filename)
}

async function uploadFile<T>(endpoint: string, file: File): Promise<T> {
  const formData = new FormData()
  formData.append('file', file)

  const headers: Record<string, string> = {}
  const token = getAuthToken()
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const tenantId = getActiveTenant()
  if (tenantId) {
    headers['X-Tenant-ID'] = tenantId
  }

  let res: Response
  try {
    res = await fetch(`${BASE_PATH}${endpoint}`, {
      method: 'POST',
      headers,
      body: formData,
    })
  } catch (netErr: any) {
    throw new Error(
      `Dosya yüklenemedi (${endpoint}): ${netErr?.message || 'Ağ bağlantısı veya CORS hatası'}`
    )
  }

  if (!res.ok) {
    if (res.status === 401) {
      clearAuthSession()
    }
    let errorDetail = `Yükleme başarısız oldu: ${res.status}`
    try {
      const body = await res.json()
      errorDetail = formatErrorDetail(body, errorDetail)
    } catch {
      // ignore
    }
    throw new Error(errorDetail)
  }

  return res.json()
}

export const api = {
  // Auth
  login: (data: { email: string; password: string; tenant_slug?: string }) =>
    request<AuthResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  platformLogin: (data: { email: string; password: string }) =>
    request<PlatformAuthResponse>('/platform/auth/login', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  getMe: () => request<CurrentUserProfile>('/auth/me'),
  logout: () => {
    clearAuthSession()
  },

  // System
  getHealth: () => request<{ status: string }>('/health'),

  // Tenants
  listTenants: () => request<Tenant[]>('/tenants'),
  getCurrentTenant: () => request<Tenant>('/tenants/current'),
  updateCurrentTenant: (data: Partial<Tenant>) =>
    request<Tenant>('/tenants/current', { method: 'PUT', body: JSON.stringify(data) }),
  createTenant: (data: Partial<Tenant>) =>
    request<Tenant>('/tenants', { method: 'POST', body: JSON.stringify(data) }),
  listTenantUsers: (tenantId: string) =>
    request<User[]>(`/tenants/${tenantId}/users`),
  createTenantUser: (tenantId: string, data: TenantUserCreatePayload) =>
    request<User>(`/tenants/${tenantId}/users`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateTenantUser: (tenantId: string, userId: string, data: TenantUserUpdatePayload) =>
    request<User>(`/tenants/${tenantId}/users/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  deleteTenantUser: (tenantId: string, userId: string) =>
    request<void>(`/tenants/${tenantId}/users/${userId}`, { method: 'DELETE' }),

  // Categories
  listCategories: () => request<Category[]>('/categories'),
  createCategory: (data: Partial<Category>) =>
    request<Category>('/categories', { method: 'POST', body: JSON.stringify(data) }),
  updateCategory: (id: string, data: Partial<Category>) =>
    request<Category>(`/categories/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteCategory: (id: string) =>
    request<void>(`/categories/${id}`, { method: 'DELETE' }),

  // Finished Products (Mamul Ürünler)
  listProducts: () => request<Product[]>('/products'),
  getProduct: (id: string) => request<Product>(`/products/${id}`),
  createProduct: (data: Partial<Product>) =>
    request<Product>('/products', { method: 'POST', body: JSON.stringify(data) }),
  updateProduct: (id: string, data: Partial<Product>) =>
    request<Product>(`/products/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteProduct: (id: string) =>
    request<void>(`/products/${id}`, { method: 'DELETE' }),
  addProductComponent: (productId: string, data: { component_type: string; component_id: string; unit_amount?: number; quantity?: number; recipe_amount?: number }) =>
    request<ProductComponent>(`/products/${productId}/components`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateProductComponent: (productId: string, componentId: string, data: { unit_amount?: number; quantity?: number; recipe_amount?: number }) =>
    request<ProductComponent>(`/products/${productId}/components/${componentId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  deleteProductComponent: (productId: string, componentId: string) =>
    request<void>(`/products/${productId}/components/${componentId}`, { method: 'DELETE' }),
  bulkUpdateProducts: (payload: BulkProductUpdatePayload) =>
    request<{ success: boolean; updated_count: number }>('/products/bulk-update', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  exportProductsExcel: () => downloadFile('/products/export-excel', { method: 'GET' }, 'urunler.xlsx'),
  importProductsExcel: (file: File) =>
    uploadFile<{ success: boolean; created: number; updated: number; total_processed: number }>(
      '/products/import-excel',
      file,
    ),

  // Trading Goods (Al-Sat Ürünler)
  listTradingGoods: () => request<TradingGood[]>('/trading-goods'),
  getTradingGood: (id: string) => request<TradingGood>(`/trading-goods/${id}`),
  createTradingGood: (data: Partial<TradingGood>) =>
    request<TradingGood>('/trading-goods', { method: 'POST', body: JSON.stringify(data) }),
  updateTradingGood: (id: string, data: Partial<TradingGood>) =>
    request<TradingGood>(`/trading-goods/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteTradingGood: (id: string) =>
    request<void>(`/trading-goods/${id}`, { method: 'DELETE' }),
  exportTradingGoodsExcel: () => downloadFile('/trading-goods/export-excel', { method: 'GET' }, 'al_sat_urunler.xlsx'),
  importTradingGoodsExcel: (file: File) =>
    uploadFile<{ success: boolean; created: number; updated: number; deleted: number; total_processed: number }>(
      '/trading-goods/import-excel',
      file,
    ),

  // Raw Materials (Hammadde)
  listRawMaterials: () => request<RawMaterial[]>('/raw-materials'),
  getRawMaterial: (id: string) => request<RawMaterial>(`/raw-materials/${id}`),
  getRawMaterialCostHistory: (id: string) =>
    request<RawMaterialCostHistory[]>(`/raw-materials/${id}/cost-history`),
  createRawMaterial: (data: Partial<RawMaterial>) =>
    request<RawMaterial>('/raw-materials', { method: 'POST', body: JSON.stringify(data) }),
  updateRawMaterial: (id: string, data: Partial<RawMaterial>) =>
    request<RawMaterial>(`/raw-materials/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteRawMaterial: (id: string) =>
    request<void>(`/raw-materials/${id}`, { method: 'DELETE' }),
  exportRawMaterialsExcel: () => downloadFile('/raw-materials/export-excel', { method: 'GET' }, 'hammaddeler.xlsx'),
  importRawMaterialsExcel: (file: File) =>
    uploadFile<{ success: boolean; created: number; updated: number; deleted: number; total_processed: number }>(
      '/raw-materials/import-excel',
      file,
    ),

  // Packagings (Ambalaj)
  listPackagings: () => request<Packaging[]>('/packagings'),
  getPackaging: (id: string) => request<Packaging>(`/packagings/${id}`),
  createPackaging: (data: Partial<Packaging>) =>
    request<Packaging>('/packagings', { method: 'POST', body: JSON.stringify(data) }),
  updatePackaging: (id: string, data: Partial<Packaging>) =>
    request<Packaging>(`/packagings/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deletePackaging: (id: string) =>
    request<void>(`/packagings/${id}`, { method: 'DELETE' }),
  exportPackagingsExcel: () => downloadFile('/packagings/export-excel', { method: 'GET' }, 'ambalajlar.xlsx'),
  importPackagingsExcel: (file: File) =>
    uploadFile<{ success: boolean; created: number; updated: number; deleted: number; total_processed: number }>(
      '/packagings/import-excel',
      file,
    ),

  // Semi-Finished Products (Yarı Mamul)
  listSemiFinished: () => request<SemiFinishedProduct[]>('/semi-finished'),
  getSemiFinished: (id: string) => request<SemiFinishedProduct>(`/semi-finished/${id}`),
  createSemiFinished: (data: Partial<SemiFinishedProduct>) =>
    request<SemiFinishedProduct>('/semi-finished', { method: 'POST', body: JSON.stringify(data) }),
  updateSemiFinished: (id: string, data: Partial<SemiFinishedProduct>) =>
    request<SemiFinishedProduct>(`/semi-finished/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteSemiFinished: (id: string) =>
    request<void>(`/semi-finished/${id}`, { method: 'DELETE' }),
  addSemiFinishedComponent: (semiFinishedId: string, data: { component_type: string; component_id: string; unit_amount?: number; quantity?: number; recipe_amount?: number }) =>
    request<ProductComponent>(`/semi-finished/${semiFinishedId}/components`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateSemiFinishedComponent: (semiFinishedId: string, componentId: string, data: { unit_amount?: number; quantity?: number; recipe_amount?: number }) =>
    request<ProductComponent>(`/semi-finished/${semiFinishedId}/components/${componentId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  deleteSemiFinishedComponent: (semiFinishedId: string, componentId: string) =>
    request<void>(`/semi-finished/${semiFinishedId}/components/${componentId}`, { method: 'DELETE' }),
  exportSemiFinishedExcel: () =>
    downloadFile('/semi-finished/export-excel', { method: 'GET' }, 'yari_mamuller_agac.xlsx'),
  importSemiFinishedExcel: (file: File) =>
    uploadFile<{ success: boolean; created: number; updated: number; deleted: number; total_processed: number }>(
      '/semi-finished/import-excel',
      file,
    ),

  // Currencies (Döviz Kurları)
  getCurrencyRates: () =>
    request<{ rates: Record<string, number>; updated_at: string | null }>('/currencies/rates'),

  // Sales Channels (Satış Kanalları)
  listSalesChannels: () => request<SalesChannel[]>('/sales-channels'),
  getSalesChannel: (id: string) => request<SalesChannel>(`/sales-channels/${id}`),
  createSalesChannel: (data: {
    name: string
    code?: string | null
    contact_email?: string | null
    preferred_currency?: string
    exchange_rate_overrides?: Record<string, number | null>
    base_sales_channel_id?: string | null
    include_kdv?: boolean
    parameters: SalesChannelParameter[]
  }) =>
    request<SalesChannel>('/sales-channels', { method: 'POST', body: JSON.stringify(data) }),
  updateSalesChannel: (
    id: string,
    data: {
      name?: string
      code?: string | null
      contact_email?: string | null
      preferred_currency?: string
      exchange_rate_overrides?: Record<string, number | null>
      base_sales_channel_id?: string | null
      include_kdv?: boolean
      parameters?: SalesChannelParameter[]
      reset_margin_param_ids?: string[]
    },
  ) =>
    request<SalesChannel>(`/sales-channels/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteSalesChannel: (id: string) =>
    request<void>(`/sales-channels/${id}`, { method: 'DELETE' }),
  assignProductToSalesChannel: (salesChannelId: string, data: { product_id: string; item_type?: string }) =>
    request<{ success: boolean; message: string }>(`/sales-channels/${salesChannelId}/products/assign`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  unassignProductFromSalesChannel: (salesChannelId: string, data: { product_id: string; item_type?: string }) =>
    request<{ success: boolean; message: string }>(`/sales-channels/${salesChannelId}/products/unassign`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Calculations
  calculateProductPrice: (params: {
    product_id?: string
    trading_good_id?: string
    miktar: number
    sales_channel_id: string
    calculation_method_override?: CalculationMethod
  }) =>
    request<CalculationResponse>('/calculate', {
      method: 'POST',
      body: JSON.stringify(params),
    }),

  calculateCustomPrice: (params: {
    miktar: number
    maliyet: number
    bilesenler: { name: string; component_type: string; birim_maliyet: number; miktar: number }[]
    parameters: { parameter_type: string; label: string; value: number; order_index: number }[]
    hesaplama_yontemi: CalculationMethod
    kdv_rate?: number
  }) =>
    request<CalculationResponse>('/calculate/custom', {
      method: 'POST',
      body: JSON.stringify(params),
    }),

  // Price List (Fiyat Listeleri)
  getPriceList: (salesChannelId?: string, status?: 'active' | 'passive') => {
    const params = new URLSearchParams()
    if (salesChannelId) params.append('sales_channel_id', salesChannelId)
    if (status) params.append('status', status)
    const qs = params.toString()
    return request<PriceListRow[]>(`/calculate/price-list${qs ? `?${qs}` : ''}`)
  },

  overridePriceListMargin: (data: {
    sales_channel_id: string
    parameter_id: string
    item_type: string
    product_id: string
    value: number | null
  }) =>
    request<{ success: boolean; parameter_id: string; value: number | null }>(
      '/calculate/price-list/override-margin',
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
    ),

  bulkOverridePriceListMargins: (data: {
    sales_channel_id: string
    parameter_id: string
    items: { item_type: string; product_id: string }[]
    value: number | null
  }) =>
    request<{ success: boolean; count: number; parameter_id: string; value: number | null }>(
      '/calculate/price-list/bulk-override-margins',
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
    ),

  exportPriceListExcel: (salesChannelId: string, productIds?: string[], tradingGoodIds?: string[]) =>
    downloadFile(
      '/calculate/export-price-list',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sales_channel_id: salesChannelId,
          product_ids: productIds && productIds.length > 0 ? productIds : null,
          trading_good_ids: tradingGoodIds && tradingGoodIds.length > 0 ? tradingGoodIds : null,
        }),
      },
      'fiyat_listesi.xlsx',
    ),

  // Demo Products (Demo Mamul Ürünler)
  listDemoProducts: () => request<DemoProduct[]>('/demo-products'),
  getDemoProduct: (id: string) => request<DemoProduct>(`/demo-products/${id}`),
  createDemoProduct: (data: Partial<DemoProduct>) =>
    request<DemoProduct>('/demo-products', { method: 'POST', body: JSON.stringify(data) }),
  updateDemoProduct: (id: string, data: Partial<DemoProduct>) =>
    request<DemoProduct>(`/demo-products/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteDemoProduct: (id: string) => request<void>(`/demo-products/${id}`, { method: 'DELETE' }),

  addDemoComponent: (demoProductId: string, data: any) =>
    request<DemoComponentItem>(`/demo-products/${demoProductId}/components`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateDemoComponent: (demoProductId: string, componentId: string, data: { unit_amount?: number | string; recipe_amount?: number | string }) =>
    request<DemoComponentItem>(`/demo-products/${demoProductId}/components/${componentId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  deleteDemoComponent: (demoProductId: string, componentId: string) =>
    request<void>(`/demo-products/${demoProductId}/components/${componentId}`, { method: 'DELETE' }),

  convertToRealProduct: (demoProductId: string, data: { code: string; replace_existing?: boolean }) =>
    request<Product>(`/demo-products/${demoProductId}/convert-to-product`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Demo Semi-Finished Products (Demo Yarı Mamuller)
  listDemoSemiFinished: () => request<DemoSemiFinishedProduct[]>('/demo-semi-finished'),
  getDemoSemiFinished: (id: string) => request<DemoSemiFinishedProduct>(`/demo-semi-finished/${id}`),
  createDemoSemiFinished: (data: Partial<DemoSemiFinishedProduct>) =>
    request<DemoSemiFinishedProduct>('/demo-semi-finished', { method: 'POST', body: JSON.stringify(data) }),
  updateDemoSemiFinished: (id: string, data: Partial<DemoSemiFinishedProduct>) =>
    request<DemoSemiFinishedProduct>(`/demo-semi-finished/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteDemoSemiFinished: (id: string) => request<void>(`/demo-semi-finished/${id}`, { method: 'DELETE' }),

  addDemoSemiFinishedComponent: (id: string, data: any) =>
    request<DemoComponentItem>(`/demo-semi-finished/${id}/components`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateDemoSemiFinishedComponent: (
    id: string,
    componentId: string,
    data: { unit_amount?: number | string; recipe_amount?: number | string }
  ) =>
    request<DemoComponentItem>(`/demo-semi-finished/${id}/components/${componentId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  deleteDemoSemiFinishedComponent: (id: string, componentId: string) =>
    request<void>(`/demo-semi-finished/${id}/components/${componentId}`, { method: 'DELETE' }),

  convertToRealSemiFinished: (id: string, data: { code: string; replace_existing?: boolean }) =>
    request<SemiFinishedProduct>(`/demo-semi-finished/${id}/convert-to-semi-finished`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Copy to Demo
  copyProductToDemo: (id: string) => request<DemoProduct>(`/products/${id}/copy-to-demo`, { method: 'POST' }),
  copySemiFinishedToDemo: (id: string) => request<DemoSemiFinishedProduct>(`/semi-finished/${id}/copy-to-demo`, { method: 'POST' }),

  // Demo Price List (Demo Fiyat Listeleri)
  getDemoPriceList: (salesChannelId?: string) =>
    request<DemoPriceListRow[]>(`/demo-calculate/price-list${salesChannelId ? `?sales_channel_id=${salesChannelId}` : ''}`),

  overrideDemoPriceListMargin: (data: {
    sales_channel_id: string
    parameter_id: string
    demo_product_id: string
    value: number | null
  }) =>
    request<{ success: boolean; message: string }>('/demo-calculate/price-list/override-margin', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  bulkOverrideDemoPriceListMargins: (data: {
    sales_channel_id: string
    parameter_id: string
    demo_product_ids: string[]
    value: number | null
  }) =>
    request<{ success: boolean; count: number }>('/demo-calculate/price-list/bulk-override-margins', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  exportDemoPriceListExcel: (salesChannelId: string, demoProductIds?: string[]) =>
    downloadFile(
      '/demo-calculate/export-price-list',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sales_channel_id: salesChannelId,
          demo_product_ids: demoProductIds && demoProductIds.length > 0 ? demoProductIds : null,
        }),
      },
      'demo_fiyat_listesi.xlsx',
    ),
}
